import React, { useRef } from 'react';

import { useCurrentTheme } from 'app/shared/theme';
import {
  Box,
  Icon,
  InputHelperText,
  InputLabel,
  InputProps,
  InputStatusIcon,
  TextInput,
  Typography,
} from 'app/shared/components/atoms';
import { ReactComponent as Lock } from 'icons/lock.svg';

interface Props extends InputProps {
  value?: string | number;
  helperText?: string | React.ReactNode | null;
  label: string;
  isDisabled?: boolean;
  displayInputValueResetButton?: boolean;
  onResetButtonClick?: () => void;
  labelColor?: string;
  isRequired?: boolean;
  persistentLeadingString?: string;
  'data-qaid'?: string;
}

export const Textfield: React.FC<React.PropsWithChildren<Props>> = ({
  onChange = () => {},
  helperText,
  hasError = false,
  label,
  isSuccessful = false,
  value = '',
  isDisabled = false,
  displayInputValueResetButton = false,
  onResetButtonClick = () => {},
  className,
  backgroundColor,
  labelColor,
  invertColors = false,
  color,
  name,
  isLocked = false,
  persistentLeadingString,
  mb,
  isRequired,
  'data-qaid': qaId = 'text-field',
  ...props
}) => {
  const theme = useCurrentTheme();

  const persistentLeadingStringRef = useRef<HTMLDivElement>(null);
  const persistentLeadingStringLength = persistentLeadingString?.length || 0;
  const persistentLeadingStringDefaultWidth =
    8 * (persistentLeadingStringLength + 1);
  const persistentLeadingStringRefWidth =
    persistentLeadingStringRef?.current?.offsetWidth ||
    persistentLeadingStringDefaultWidth;

  const displayResetButton =
    displayInputValueResetButton && !isSuccessful && !!value && !hasError;

  const inputStatusIconType = () => {
    if (hasError) {
      return 'ERROR';
    }
    if (displayResetButton) {
      return 'RESET';
    }
    if (isSuccessful) {
      return 'SUCCESS';
    }

    return null;
  };

  const persistentLeadingStringFontColor = hasError
    ? theme.colors.messaging.error[invertColors ? 'light' : 'dark']
    : undefined;

  return (
    <Box
      position="relative"
      w="100%"
      flexDirection="column"
      alignItems="stretch"
      className={className}
      data-qaid={qaId}
      mb={mb}
    >
      <InputLabel
        name={name}
        label={label}
        labelColor={labelColor}
        hasError={hasError}
        invertColors={labelColor === theme.colors.primary.white.base}
        isRequired={isRequired}
        data-qaid={`${qaId}-label`}
      />
      <Box flexDirection="row" alignItems="center">
        {!isLocked && (
          <InputStatusIcon
            onClickResetInput={onResetButtonClick}
            iconType={inputStatusIconType()}
            invertColors={invertColors}
            backgroundColor={backgroundColor}
            data-qaid={`${qaId}-status-icon`}
          />
        )}
        {isLocked && (
          <Box position="absolute" right={`${theme.ruler[4]}px`}>
            <Icon
              size="sm"
              icon={Lock}
              strokeColor={theme.colors.primary.black.lighter}
              data-qaid={`${qaId}-lock-icon`}
            />
          </Box>
        )}
        {persistentLeadingString && (
          <Box
            position="absolute"
            left={`${theme.ruler[3]}px`}
            display="flex"
            ref={persistentLeadingStringRef}
          >
            <Typography
              as="span"
              fontWeight="medium"
              lineHeight="120%"
              color={persistentLeadingStringFontColor}
            >
              {persistentLeadingString}&nbsp;
            </Typography>
          </Box>
        )}
        <TextInput
          data-qaid={`${qaId}-input`}
          disabled={isDisabled}
          hasError={hasError}
          isSuccessful={isSuccessful}
          value={value}
          onChange={onChange}
          readOnly={isLocked}
          isLocked={isLocked}
          backgroundColor={backgroundColor}
          invertColors={invertColors}
          color={color}
          name={name}
          aria-required={isRequired}
          pl={
            persistentLeadingString
              ? `calc(${persistentLeadingStringRefWidth}px + ${theme.ruler[3]}px)`
              : 3
          }
          {...props}
        />
      </Box>
      {helperText && (
        <InputHelperText
          data-qaid={`${qaId}-helper-text`}
          isDisabled={isDisabled}
          hasError={hasError}
          isSuccessful={isSuccessful}
          invertColors={labelColor === theme.colors.primary.white.base}
        >
          {helperText}
        </InputHelperText>
      )}
    </Box>
  );
};
